import { Subscribable } from '@benzinga/subscribable';
import { SafeError } from '@benzinga/safe-await';
import { LoggingManager, Session } from '@benzinga/session';
import { PlaySoundEnvironment } from './environment';

enum AudioContextState {
  Running = 'running',
}

interface SoundAlertError {
  type: 'error';
  errorType: 'play_sound_error';
  error: SafeError;
}

export class PlaySoundManager extends Subscribable<SoundAlertError> {
  private isAutoPlayAllowed: boolean;
  private audioContext: AudioContext | null;
  private sounds: Record<string, string>;
  private session: Session;

  constructor(session: Session) {
    super();
    /*
      Why use AudioContext:
      When user opens some page and keeps it opened without any clicks on content and that page has UI
      that can produce sounds, if that page try to play some audio there'll be an error because of the
      browsers autoplay policy.
      So we can check state of AudioContext that should be running for already interacted page,
      if it's not, we will try resume it as a Promise and check it again.
    */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore window actually has webkitAudioContext in Safari
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = AudioContext ? new AudioContext() : null;
    this.isAutoPlayAllowed = this.audioContext?.state === AudioContextState.Running;
    this.sounds = session.getEnvironment(PlaySoundEnvironment).sounds;
    this.session = session;

    // Such checks needed because test environment has no AudioContext
    if (this.audioContext) {
      this.audioContext.onstatechange = this.updateAutoPlayStatus;
    }
  }

  public static getName = () => 'benzinga-play-sound';

  public playSound = (soundName: string, source: string, reason: string, volume = 100): void => {
    const sound = this.sounds[soundName];

    if (!sound) {
      return;
    }

    this.session
      .getManager(LoggingManager)
      // Routine: every alert plays a sound. Only surfaces when verbosity is raised to debug.
      .log('debug', { category: source, message: `Playing sound '${soundName}': ${reason}` }, ['console']);

    if (this.isAutoPlayAllowed) {
      this.playAudio(sound, volume);
    } else {
      this.resumeAudioContextAndPlayAudio(sound, volume);
    }
  };

  playAudio = (sound: string, volume: number): void => {
    const audio = new Audio(sound);
    audio.volume = volume / 100;
    const promise = audio.play();

    // We're checking to see if the promise exists because of what seems to be a bug
    // specific to Edge 16, that audio.play() may not have actually returned a promise.
    if (promise) {
      promise.catch(error =>
        this.dispatch({
          error: new SafeError('play-sound', 'could not play audio', error),
          errorType: 'play_sound_error',
          type: 'error',
        }),
      );
    } else {
      this.dispatch({
        error: new SafeError('play-sound', 'no promise created by audio.play()'),
        errorType: 'play_sound_error',
        type: 'error',
      });
    }
  };

  updateAutoPlayStatus = (): void => {
    this.isAutoPlayAllowed = this.audioContext?.state === AudioContextState.Running;
  };

  resumeAudioContextAndPlayAudio = (sound: string, volume: number): void => {
    this.audioContext
      ?.resume()
      .then(() => {
        this.updateAutoPlayStatus();

        if (this.isAutoPlayAllowed) {
          this.playAudio(sound, volume);
        }
      })
      .catch(error => {
        this.dispatch({
          error: new SafeError('play-sound', 'could not play audio', error),
          errorType: 'play_sound_error',
          type: 'error',
        });
      });
  };
}
